import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../api';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sedeActiva, setSedeActiva] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [colorSede, setColorSede] = useState('#14B8A6'); // Default teal color
  const [sedeAforo, setSedeAforo] = useState(26); // Default capacity

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      loadSedes();
      // Initialize sede activa based on user permissions
      initializeSedeActiva();
    }
  }, [user]);

  // ANTI "PERMISO FANTASMA": el user (con permisos_granulares derivados de
  // roles_v3) sólo vive en estado de React y se refresca desde /auth/me al
  // montar la app. En una sesión SPA que permanece abierta mucho tiempo, un
  // cambio de permisos hecho por un admin no se reflejaría hasta un recargo
  // manual (percibido como "hay que volver a entrar"). Refrescamos /auth/me
  // también cuando la pestaña recupera el foco / vuelve a ser visible, de
  // forma silenciosa y defensiva (si falla, se mantiene el user actual).
  // No se cachea el user en localStorage a propósito: la fuente de verdad es
  // siempre /auth/me, para no reintroducir el propio caché obsoleto que este
  // arreglo pretende evitar.
  useEffect(() => {
    const onFocusOrVisible = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('token')) return;
      refreshUser({ silent: true });
    };
    window.addEventListener('focus', onFocusOrVisible);
    document.addEventListener('visibilitychange', onFocusOrVisible);
    return () => {
      window.removeEventListener('focus', onFocusOrVisible);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
    };
  }, []);

  // Refresca el user desde /auth/me. Defensivo: sólo cierra sesión ante 401/403
  // (falta de autorización real). Ante cualquier otro fallo (red, 502/504…)
  // mantiene el user cacheado en estado y no toca la sesión.
  // opts.silent evita spamear logout/console en refrescos de fondo.
  const refreshUser = async ({ silent = false } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) {
      delete axios.defaults.headers.common['Authorization'];
      return null;
    }
    // CRÍTICO: Configurar axios defaults ANTES de cualquier llamada
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Reintentar /auth/me ante fallos TRANSITORIOS (red, timeout, 5xx) antes de
    // echar a nadie. Un 401/403 REAL expulsa a la PRIMERA (blindaje intacto); solo
    // los transitorios reintentan. Así un hipo de red —típico al volver el foco a la
    // pestaña— deja de expulsar a media faena. MITIGA la molestia, NO cura la causa
    // del 401 (para eso está el logging del motivo). Ann/Director 2026-09-02.
    const MAX_INTENTOS = 3;
    let ultimoError = null;
    for (let intento = 1; intento <= MAX_INTENTOS; intento++) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        return response.data;
      } catch (error) {
        ultimoError = error;
        const status = error.response?.status;
        // 401/403 = rechazo REAL de autorización → NO reintentar, cerrar sesión ya.
        if (status === 401 || status === 403) {
          const motivo = error.response?.data?.detail?.message || error.response?.data?.detail || 'sin detalle';
          console.warn(`[SESION] /auth/me devolvió ${status} (${motivo}) — cierre de sesión REAL${silent ? ' (refresco de fondo)' : ''}.`);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          return null;
        }
        // Transitorio (sin respuesta, timeout, 5xx): reintentar SIN tocar la sesión.
        if (!silent) console.warn(`[SESION] /auth/me falló de forma transitoria (status=${status || 'sin respuesta'}) intento ${intento}/${MAX_INTENTOS}. No se cierra sesión.`);
        if (intento < MAX_INTENTOS) await new Promise((r) => setTimeout(r, 800 * intento));
      }
    }
    // Agotados los reintentos por causa TRANSITORIA: mantener el user actual (no expulsar).
    if (!silent) console.error('[SESION] /auth/me no respondió tras varios intentos (transitorio). Se conserva la sesión.', ultimoError);
    return null;
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      await refreshUser();
    } else {
      // No hay token - asegurar que axios no tenga header de auth
      delete axios.defaults.headers.common['Authorization'];
    }
    setLoading(false);
  };

  // BLINDAJE ESPACIO CLIENTE (F2) · token de dispositivo de confianza, por
  // cuenta (una tablet familiar con 2 clientes no pisa el token del otro).
  const deviceTokenKey = (email) => `mpd_device_token::${(email || '').toLowerCase().trim()}`;

  const login = async (email, password) => {
    // F2 · enviar el device token si este navegador ya es de confianza
    const headers = {};
    const dt = localStorage.getItem(deviceTokenKey(email));
    if (dt) headers['X-Device-Token'] = dt;

    const response = await axios.post(`${API}/auth/login`, {
      email,
      password
    }, { headers });
    const token = response.data.access_token;
    const userData = response.data.user;

    // F2 · bootstrap del 1er dispositivo: el backend devuelve device_token
    if (response.data.device_token) {
      localStorage.setItem(deviceTokenKey(email), response.data.device_token);
    }

    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);

    return { access_token: token, user: userData };
  };

  // PUENTE-WP-CLUB · canjea el token de puente firmado por WordPress por la
  // sesión propia del CRM (mismo almacenamiento que login normal). La
  // autenticación real la hizo WordPress; el backend valida firma/exp/iss.
  const loginWithWordpressToken = async (bridgeToken) => {
    const response = await axios.post(`${API}/auth/wordpress-token`, {
      token: bridgeToken,
    });
    const token = response.data.access_token;
    const userData = response.data.user;

    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);

    return { access_token: token, user: userData };
  };

  // F2 · verificación del código 2FA en dispositivo nuevo: registra el
  // dispositivo (guardamos su token) y repite el login ya como confianza.
  const verify2FA = async (email, password, codigo) => {
    const r = await axios.post(`${API}/auth/login-2fa`, { email, password, codigo });
    if (r.data?.device_token) {
      localStorage.setItem(deviceTokenKey(email), r.data.device_token);
    }
    return login(email, password);
  };

  // Cierre SOLO en este navegador: no llama al servidor, así NO invalida las otras
  // sesiones del titular (incidente Ann 12-sep-2026). Lo usa el cierre por inactividad.
  const logoutLocal = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setSedeActiva(null);
    setSedes([]);
  };

  const logout = async () => {
    // Invalidación REAL en servidor (AUTH-CORE · token_version): al cerrar sesión
    // el token deja de valer aunque alguien lo hubiera copiado. Fire-and-forget:
    // si el servidor no responde, igual cerramos en el navegador. (Ann 18-jul).
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* cerramos igual */ }
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setSedeActiva(null);
    setSedes([]);
  };

  const loadSedes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/sedes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSedes(response.data);
    } catch (error) {
      console.error('Error loading sedes:', error);
    }
  };

  const initializeSedeActiva = () => {
    if (!user) return;

    // Get stored sede from localStorage or use default based on permissions
    const storedSede = localStorage.getItem('sedeActiva');

    console.log('🔍 Initializing sedeActiva for user:', user.email, 'role:', user.role);

    if (user.role === 'admin') {
      // Admin can access all sedes, default to stored or MUNTANER
      const sede = storedSede || 'MUNTANER';
      console.log('✅ Admin - Setting sedeActiva to:', sede);
      setSedeActiva(sede);
    } else if (user.role === 'cliente') {
      // Client belongs to one sede only
      const sede = user.sede || 'MUNTANER';
      console.log('✅ Cliente - Setting sedeActiva to:', sede);
      setSedeActiva(sede);
    } else {
      // Any other staff role (editor, gestor, fisioterapeuta, etc.)
      const sedesPermitidas = user.sedes_permitidas || ['MUNTANER'];
      console.log('📋 Staff sedes permitidas:', sedesPermitidas);

      if (sedesPermitidas.length === 0) {
        // Default to MUNTANER if no sedes configured
        console.log('⚠️ No sedes configured, defaulting to MUNTANER');
        setSedeActiva('MUNTANER');
        return;
      }

      if (sedesPermitidas.length === 1) {
        // Single sede - no choice
        console.log('✅ Staff (single sede) - Setting sedeActiva to:', sedesPermitidas[0]);
        setSedeActiva(sedesPermitidas[0]);
      } else {
        // Multiple sedes - use stored or first permitted
        const validStored = storedSede && sedesPermitidas.includes(storedSede);
        const sede = validStored ? storedSede : sedesPermitidas[0];
        console.log('✅ Staff (multiple sedes) - Setting sedeActiva to:', sede);
        setSedeActiva(sede);
      }
    }
  };

  const cambiarSede = (nuevaSede) => {
    // Validate permissions before changing
    if (user.role === 'admin') {
      setSedeActiva(nuevaSede);
      localStorage.setItem('sedeActiva', nuevaSede);
    } else if (user.role !== 'cliente') {
      // Any staff role (editor, gestor, fisioterapeuta, etc.)
      const sedesPermitidas = user.sedes_permitidas || [];
      if (sedesPermitidas.includes(nuevaSede)) {
        setSedeActiva(nuevaSede);
        localStorage.setItem('sedeActiva', nuevaSede);
      }
    }
  };

  // Update color and aforo when sede changes
  useEffect(() => {
    if (sedeActiva && sedes.length > 0) {
      const sedeInfo = sedes.find(s => s.nombre === sedeActiva);
      if (sedeInfo) {
        setColorSede(sedeInfo.color_principal);
        setSedeAforo(sedeInfo.aforo_default);
      }
    }
  }, [sedeActiva, sedes]);

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role && user.role !== 'cliente'; // Any non-client role (editor, gestor, fisioterapeuta, etc.)
  const isEditor = isStaff; // Alias for backward compatibility
  const canDelete = isAdmin;
  const canImportExport = isAdmin;

  // Check if user can access multiple sedes (for showing selector)
  const canSelectSede = user?.role === 'admin' || (isStaff && (user?.sedes_permitidas || []).length > 1);

  // Check if user has permission to view a specific tab
  const hasTabPermission = (tabId) => {
    // If user data is not fully loaded, return false to prevent rendering issues
    if (!user) return false;

    // Admin always has access to all tabs
    if (isAdmin) return true;

    // Any staff role (editor, gestor, fisioterapeuta, etc.) needs to check permisos_granulares
    if (isStaff) {
      // Check new granular permissions system first
      const permisosGranulares = user?.permisos_granulares || {};

      // If permisos_granulares exists and has the tabId
      if (permisosGranulares && Object.keys(permisosGranulares).length > 0) {
        if (permisosGranulares[tabId]) {
          // Has granular permissions - check if has 'ver' permission
          if (permisosGranulares[tabId].ver === true) {
            return true;
          }
        }

        // SPECIAL CASE: For tabs with submenus (like gestoria, clinica, laboral)
        // Check if user has 'ver' permission on ANY submenu
        // This allows access to the main tab if they can view at least one submenu
        const tabsWithSubmenus = ['gestoria', 'clinica', 'laboral', 'configuracion-general'];
        if (tabsWithSubmenus.includes(tabId)) {
          const prefix = tabId === 'configuracion-general' ? 'configuracion' : tabId;
          for (const key of Object.keys(permisosGranulares)) {
            if (key.startsWith(`${prefix}.`) && permisosGranulares[key]?.ver === true) {
              return true;
            }
          }
        }

        // Tab not in granular permissions = no access
        return false;
      }

      // Fallback to old system for backward compatibility
      const permisosPestanas = user?.permisos_pestanas || [];
      if (permisosPestanas.length > 0) {
        return permisosPestanas.includes(tabId);
      }

      // If no permissions are set yet, return false (will show message until refresh)
      // This prevents the "flash" of all tabs before permissions load
      return false;
    }

    // Clients don't have tab permissions (different UI)
    return false;
  };

  // Check if user can create in a specific tab
  const canCreate = (tabId) => {
    if (isAdmin) return true;
    if (!isStaff) return false;

    const permisosGranulares = user?.permisos_granulares || {};
    return permisosGranulares[tabId]?.crear === true;
  };

  // Check if user can edit in a specific tab
  const canEdit = (tabId) => {
    if (isAdmin) return true;
    if (!isStaff) return false;

    const permisosGranulares = user?.permisos_granulares || {};
    return permisosGranulares[tabId]?.editar === true;
  };

  // Check if user can delete in a specific tab (more restrictive)
  const canDeleteInTab = (tabId) => {
    if (isAdmin) return true;
    if (!isStaff) return false;

    const permisosGranulares = user?.permisos_granulares || {};
    return permisosGranulares[tabId]?.eliminar === true;
  };

  // Generic permission check for any resource and action
  const hasPermission = (resourceId, action = 'ver') => {
    if (!user) return false;
    if (isAdmin) return true;
    if (!isStaff) return false;

    const permisosGranulares = user?.permisos_granulares || {};
    const permiso = permisosGranulares[resourceId];

    if (!permiso) return false;

    switch (action) {
      case 'ver': return permiso.ver === true;
      case 'crear': return permiso.crear === true;
      case 'editar': return permiso.editar === true;
      case 'eliminar': return permiso.eliminar === true;
      default: return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    loginWithWordpressToken,
    verify2FA,
    logout,
    logoutLocal,
    isAdmin,
    isStaff,
    isEditor, // Alias for backward compatibility
    canDelete,
    canDeleteInTab,
    canCreate,
    canEdit,
    canImportExport,
    setUser,
    refreshUser,
    sedeActiva,
    sedes,
    colorSede,
    sedeAforo,
    cambiarSede,
    canSelectSede,
    hasTabPermission,
    hasPermission,
    refreshSedes: loadSedes
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
